import React, { ReactElement, useEffect, useState } from 'react';
import cx from 'classnames';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SprintProductChapter } from '@data';
import { getFullNameOrEmail, toFixedHours } from '@app/utils';

/**
 * One role: a row with the role's total hours and, when expanded, a per-person table.
 *
 * The prototype prices this — a rate column, a cost column, and a `rateFor` resolver passed in. All of
 * it is out under the financial quarantine. A cost column goes back as a third column in the table
 * below, and the prototype's version of this file is the reference for whoever picks that up.
 */
const RoleCard = ({
  chapter,
  label,
  expandAll,
}: {
  chapter: SprintProductChapter;
  label: string;
  expandAll: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(expandAll), [expandAll]);
  const members = chapter.members.slice().sort((a, b) => b.totalMinutes - a.totalMinutes);
  const roleHours = chapter.totalMinutes / 60;

  return (
    // pr-8 (vs pl-4) insets the totals to match the day totals in the other view, which sit inside the
    // nested role cards — so the numbers line up when switching between the two.
    <div className="py-3 pl-4 pr-8">
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <span className="flex min-w-0 items-center gap-2">
          <ChevronRight
            className={cx(
              'size-4 shrink-0 text-muted-foreground transition-transform duration-300',
              open && 'rotate-90',
            )}
          />
          <span className="truncate text-base font-semibold text-foreground">{label}</span>
        </span>
        <span className="shrink-0 text-base font-semibold tabular-nums text-foreground">
          {toFixedHours(roleHours)}h
        </span>
      </button>

      <div
        className={cx(
          'grid transition-[grid-template-rows] duration-300 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <table className="mt-4 w-full table-fixed text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <th className="pb-2 text-left font-semibold">{t('productDashboard.teamMember')}</th>
                <th className="w-28 pb-2 text-right font-semibold">{t('productDashboard.teamHoursCol')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {members.map(member => (
                <tr key={member.member.id}>
                  <td className="py-2.5 pr-4 text-foreground">{getFullNameOrEmail(member.member.user)}</td>
                  <td className="py-2.5 text-right tabular-nums text-foreground">
                    {toFixedHours(member.totalMinutes / 60)}h
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/**
 * The sprint read by team: one row per role that has time on it, most hours first, in a single box.
 *
 * `expandAll` is the caller's to own. Both screens that render this also render `DayLog`, and the
 * control has to survive switching between them — a toggle living in here would reset on every switch
 * and the two views would stop reading as one report.
 */
const RoleSummary = ({
  groups,
  resolveLabel,
  expandAll,
}: {
  groups: SprintProductChapter[];
  resolveLabel: (key: string) => string;
  expandAll: boolean;
}): ReactElement => (
  <div className="divide-y divide-border rounded-xl border border-border bg-card">
    {groups.map(chapter => (
      <RoleCard key={chapter.chapter} chapter={chapter} label={resolveLabel(chapter.chapter)} expandAll={expandAll} />
    ))}
  </div>
);

export default RoleSummary;
